import React, { useEffect, useMemo } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { FormControl, FormHelperText, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import Button from "@components/Button/Button";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import { t } from "i18next";
import debounce from "lodash.debounce";
import { Controller, useFormContext, useWatch } from "react-hook-form";

interface Props {
  name: string;
  sectionLabel: string;
  label: string;
  placeholder?: string;
  testId: string;
  onFieldChange: (key: string, newArr: string[]) => void;
  minItems?: number;
  maxItems?: number;
  buttonLabel?: string;
  maxChars?: number;
  type?: "text" | "autocomplete";
  options?: { label: string; value: string }[];
}

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

const isPrintableKey = (e: React.KeyboardEvent) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const helperTextSx = { ml: 0, minHeight: "20px" };

export function RepeatablePrimitiveField({
  name,
  sectionLabel,
  label,
  placeholder,
  testId,
  onFieldChange,
  minItems = 1,
  maxItems,
  buttonLabel = "Add New",
  maxChars,
  type = "text",
  options = [],
}: Readonly<Props>) {
  const {
    control,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext<{ [K in typeof name]: string[] }>();

  // Debounced updater: only fires onFieldChange after 300ms of no further calls
  const debouncedChange = useMemo(
    () =>
      debounce((arr: string[]) => {
        onFieldChange(name, arr);
      }, 300),
    [name, onFieldChange],
  );

  // cancel debounce on unmount
  useEffect(() => () => debouncedChange.cancel(), [debouncedChange]);

  // Watch the array in real time
  const items = useWatch({ control, name }) || [];

  // On mount, ensure at least minItems exist
  useEffect(() => {
    if (items.length < minItems) {
      const next = [...items, ...new Array(minItems - items.length).fill("")];
      setValue(name, next, { shouldValidate: true, shouldDirty: true });
      onFieldChange(name, next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once

  return (
    <FormControl
      fullWidth
      sx={(theme) => ({
        "mb": 3,
        "& .MuiFormLabel-root": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-focused": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-error": {
          color: theme.palette.text.primary,
        },
      })}
    >
      <FormSection
        title={sectionLabel}
        isFullWidth
        button={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              const nextArr = [...items, ""];
              setValue(name, nextArr, { shouldValidate: true, shouldDirty: true });
              onFieldChange(name, nextArr);
            }}
            variant="outlined"
            label={<span className="normal-case font-medium">{buttonLabel}</span>}
            testId={`${testId}-add-button`}
            disabled={!!maxItems && items.length >= maxItems}
          />
        }
      >
        <Stack spacing={2}>
          {items.map((_, idx) =>
            (() => {
              const fieldErrorMessage = (errors?.[name]?.[idx]?.message as string) ?? undefined;

              return (
                <div className="col-span-2" key={idx}>
                  <CustomFormLabel
                    data-testid={`${label}-${idx}`}
                    sx={{ mb: 1 }}
                    error={!!errors?.[name]?.[idx]}
                  >{`${label} ${idx + 1}`}</CustomFormLabel>
                  <Stack direction="row" spacing={2} alignItems="center" className="mt-0">
                    <Controller
                      name={`${name}.${idx}`}
                      control={control}
                      render={({ field }) =>
                        type === "autocomplete" ? (
                          <Autocomplete
                            value={options.find((o) => o.value === field.value) || null}
                            options={options}
                            placeholder={placeholder || label}
                            error={!!errors?.[name]?.[idx]}
                            testId={`${testId}-${idx}`}
                            fullWidth
                            onChange={(opt: { value?: unknown } | null) => {
                              const selected = opt?.value != null ? String(opt.value) : "";
                              field.onChange(selected);
                              const nextArr = getValues(name).map((s, i) =>
                                i === idx ? selected : s,
                              );
                              debouncedChange(nextArr);
                            }}
                          />
                        ) : (
                          <Input
                            {...field}
                            placeholder={placeholder || label}
                            fullWidth
                            error={!!errors?.[name]?.[idx]}
                            testId={`${testId}-${idx}`}
                            slotProps={{ htmlInput: { maxLength: maxChars } }}
                            onKeyDown={(e) => {
                              if (!maxChars) return;
                              if (!isPrintableKey(e)) return;

                              const current = (getValues(name)?.[idx] ?? "") as string;
                              if (current.length >= maxChars) {
                                e.preventDefault();
                                setError(`${name}.${idx}`, {
                                  type: "max",
                                  message: t("form-validation.max-length", {
                                    label,
                                    max: maxChars,
                                  }),
                                });
                              }
                            }}
                            onChange={(e) => {
                              const nextVal = (e as React.ChangeEvent<HTMLInputElement>).target
                                .value;
                              field.onChange(nextVal);
                              const nextArr = getValues(name).map((s, i) =>
                                i === idx ? nextVal : s,
                              );
                              debouncedChange(nextArr);
                            }}
                            onBlur={() => {
                              const arr = getValues(name);
                              const prev = (arr[idx] ?? "") as string;
                              const trimmed = prev.trim();
                              if (trimmed !== prev) {
                                const nextArr = arr.map((s, i) => (i === idx ? trimmed : s));
                                setValue(name, nextArr, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                                debouncedChange.cancel();
                                onFieldChange(name, nextArr);
                              }
                              field.onBlur();
                            }}
                          />
                        )
                      }
                    />

                    {idx > 0 && (
                      <RemoveButton
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          const nextArr = items.filter((_, i) => i !== idx);
                          setValue(name, nextArr, { shouldValidate: true, shouldDirty: true });
                          onFieldChange(name, nextArr);
                        }}
                        testId={`${name}-${idx}-remove`}
                        disableElevation
                        variant="contained"
                        label={<span className="normal-case font-medium">Remove</span>}
                      />
                    )}
                  </Stack>
                  <FormHelperText
                    error={Boolean(fieldErrorMessage)}
                    sx={helperTextSx}
                    data-testid={`${testId}-${idx}-helper-text`}
                  >
                    {fieldErrorMessage ?? "\u00A0"}
                  </FormHelperText>
                </div>
              );
            })(),
          )}
        </Stack>
      </FormSection>
    </FormControl>
  );
}

export default RepeatablePrimitiveField;
